import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { GLOBAL } from 'src/app/_config/config';
import { CreateProfileUser, ProfileUpdate, Profiles, ResponseRequest, User } from 'src/app/interfaces/authorizations.interfaces';

/**
 * Servicio que administra los perfiles, usuarios y autorizaciones del sistema,
 * permitiendo crear, consultar, actualizar y eliminar perfiles y usuarios, así
 * como gestionar los permisos asociados a cada perfil.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthorizationsService {
  userTableFormat: any[] = [];
  userTableFormatFilter: any[] = [];

  constructor(
    private http: HttpClient
  ) { }

  /**
   * Obtiene la lista de perfiles disponibles.
   * @returns Observable con la lista de perfiles.
   */
  getProfiles(): Observable<Profiles[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.authorizations.profiles}`;
    return this.http.get<Profiles[]>(url);
  }

  /**
   * Crea un nuevo perfil.
   * @param group_name - Nombre del grupo o perfil.
   * @param description - Descripción del perfil.
   * @returns Observable con la respuesta de la creación.
   */
  createProfile(group_name: string, description: string): Observable<CreateProfileUser> {
    const url: string = `${GLOBAL.url}${GLOBAL.authorizations.createProfile}`;
    return this.http.post<CreateProfileUser>(url, {group_name, description});
  }

  /**
   * Obtiene la lista de usuarios.
   * @returns Observable con la lista de usuarios.
   */
  getUsers(): Observable<User[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.authorizations.user.list}`;
    return this.http.get<User[]>(url);
  }

  /**
   * Crea un nuevo usuario; incluye el identificador de cliente solo cuando el
   * perfil corresponde a clientes.
   * @param group_name - Nombre del grupo o perfil del usuario.
   * @param user_email - Correo electrónico del usuario.
   * @param customer_id - Identificador del cliente o `null` si no aplica.
   * @returns Observable con la respuesta de la creación.
   */
  createUser(group_name: string, user_email: string, customer_id: number | null): Observable<CreateProfileUser> {
    const url: string = `${GLOBAL.url}${GLOBAL.authorizations.user.create}`;
    if (group_name !== 'clientes') {
      return this.http.post<CreateProfileUser>(url, {group_name, user_email});
    }
    return this.http.post<CreateProfileUser>(url, {group_name, user_email, customer_id});
  }

  /**
   * Actualiza un usuario; incluye el identificador de cliente solo cuando el
   * perfil corresponde a clientes.
   * @param group_name - Nombre del grupo o perfil del usuario.
   * @param user_email - Correo electrónico del usuario.
   * @param customer_id - Identificador del cliente o `null` si no aplica.
   * @returns Observable con la respuesta de la actualización.
   */
  updateUser(group_name: string, user_email: string, customer_id: number | null): Observable<ResponseRequest> {
    const url: string = `${GLOBAL.url}${GLOBAL.authorizations.user.update}`;
    if (group_name !== 'clientes') {
      return this.http.put<CreateProfileUser>(url, {group_name, user_email});
    }
    return this.http.put<ResponseRequest>(url, {group_name, user_email, customer_id});
  }

  /**
   * Elimina un usuario.
   * @param user_email - Correo electrónico del usuario a eliminar.
   * @returns Observable con la respuesta de la eliminación.
   */
  deleteUser(user_email: string): Observable<ResponseRequest> {
    const url: string = `${GLOBAL.url}${GLOBAL.authorizations.user.delete}`;
    const headers = {
      headers: new HttpHeaders(),
      body: {
        user_email
      }
    };
    return this.http.delete<ResponseRequest>(url, headers);
  }

  /**
   * Obtiene las autorizaciones asociadas a un perfil.
   * @param profile - Nombre del perfil a consultar.
   * @returns Observable con las autorizaciones del perfil.
   */
  getAuthorizationsByProfile(profile: string) {
    const url: string = `${GLOBAL.url}${GLOBAL.authorizations.getByProfile}${profile}`;
    return this.http.get(url);
  }

  /**
   * Actualiza las autorizaciones de un perfil.
   * @param profile - Datos del perfil con las autorizaciones a actualizar.
   * @returns Observable con la respuesta de la actualización.
   */
  updateAuthorizationsByProfile(profile: ProfileUpdate): Observable<ResponseRequest> {
    const url: string = `${GLOBAL.url}${GLOBAL.authorizations.update}`;
    return this.http.post<ResponseRequest>(url, profile);
  }
}
